import { DefaultRootState } from '@reduxLocal/persistState';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Bubble, GiftedChat, IMessage, InputToolbarProps, Message, MessageImage, MessageProps, MessageText, Send, SystemMessage } from 'react-native-gifted-chat';
import { useDispatch, useSelector } from 'react-redux';
import ErrorBoundary from '@utils/ErrorBoundary';
import InputToolbar from './InputToolbar';
import { heightPercentageToDP, widthPercentageToDP } from './Dimensions';
import { Text, View, Spinner, Image } from 'tamagui';
import { handleMessage } from '../utils/handleMessage';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { LOADED_MESSAGES, UPDATE_CONVERSATION_ID } from '@reduxLocal/actions/types';
import { FONTS } from './Misc';
import useSound from '@utils/messagePing';
import { useMessageList } from '../Context/MessageListContext';
import { Dimensions, Platform } from 'react-native';
import { isTablet } from 'react-native-device-info';
import { useQueryClient } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import ParsedTextWithReferences from '@navigation/screens/MidLevel/EasyModeScreen/Components/ParsedTextWithReferences';
import { useNavigation } from '@react-navigation/native';
import type { ParsedReference } from '@navigation/screens/MidLevel/EasyModeScreen/utils/chatUtils';

const SCREEN_WIDTH = Dimensions.get('window').width;
interface ChatContainerProps {
  selectedConversation?: {
    _id?: string;
    id?: string;
    participantId?: string;
    conversationId?: string;
    avatar?: string | { img?: string };
  };
  socket: any;
}

type ExtendedMessage = IMessage & {
  seen?: boolean;
  delivered?: boolean;
  pending?: boolean;
  sent?: boolean;
  received?: boolean;
  seenBy?: string[]; // Array of user IDs who have seen this message (for group messaging)
};

const ChatContainer = ({
  selectedConversation,
  socket,
}: ChatContainerProps) => {
  const messageLoading = useSelector((state: DefaultRootState) => state.UI.loadingMessages);
  const userData = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData);
  const messageModalDisplayed = useSelector((state: DefaultRootState) => state.modals.messageModal.modalDisplayed);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { dispatch: contextDispatch } = useMessageList();
  const [data, setData] = React.useState<any[]>([]);
  const [messageArray, setMessageArray] = React.useState<ExtendedMessage[]>([]);
  const playSound = useSound();
  const [layoutWidth, setLayoutWidth] = useState<number>(0);
  const convoId = useSelector((state: DefaultRootState) => state.modals.messageModal.content?.id || '')
  const [conversationId, setConversationId] = useState<string | null>(selectedConversation?.conversationId || convoId);
  const queryClient = useQueryClient();
  const markedSeenRef = useRef<Set<string>>(new Set());

  // Store handler references so we can remove them properly in cleanup
  const responseHandlerRef = useRef<((e: IMessage & { convoId: string; conversationId?: string }) => void) | null>(null);
  const deliveredHandlerRef = useRef<((e: { id: string }) => void) | null>(null);

  const selectedConversationRef = useRef(selectedConversation);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    if (selectedConversation?.conversationId) {
      console.log('📥 ChatContainer: Setting conversationId from selectedConversation:', selectedConversation.conversationId);
      setConversationId(selectedConversation.conversationId);
    } else if (selectedConversation?.id) {
      // Fallback to id if conversationId is not set
      console.log('📥 ChatContainer: No conversationId, using id:', selectedConversation.id);
    }
  }, [selectedConversation?.conversationId, selectedConversation?.id]);

  useEffect(() => {
    // Create and store the response handler
    const responseHandler = (e: IMessage & { convoId: string; conversationId?: string; userId?: string }) => {
      console.log("received message", e);

      // Get the sender's user ID from the message
      const senderUserId = e.userId || e.user?._id || (e.user as any)?._id;
      const conversationId = e.convoId || e.conversationId;
      const isCurrentConversation = e.convoId === selectedConversation?.conversationId || e.conversationId === selectedConversation?.conversationId;

      // If chat is not open OR it's a different conversation, update badge
      if (!messageModalDisplayed && senderUserId && conversationId) {
        console.log('📬 [ChatContainer] Chat not open, updating badge for sender:', {
          senderUserId,
          conversationId,
          isCurrentConversation,
        });
        contextDispatch({
          type: 'UPDATE_BADGE',
          userId: senderUserId,
          amount: 1,
          event: {
            convoId: conversationId,
            conversationId: conversationId,
            ...e,
          },
        });

        // Also invalidate the query to refresh the user list
        queryClient.invalidateQueries({ queryKey: ['all-users'] });
      } else if (messageModalDisplayed && !isCurrentConversation && senderUserId && conversationId) {
        // Chat is open but this is a different conversation - still update badge
        console.log('📬 [ChatContainer] Different conversation, updating badge:', {
          senderUserId,
          conversationId,
          currentConversation: selectedConversation?.conversationId,
        });
        contextDispatch({
          type: 'UPDATE_BADGE',
          userId: senderUserId,
          amount: 1,
          event: {
            convoId: conversationId,
            conversationId: conversationId,
            ...e,
          },
        });

        queryClient.invalidateQueries({ queryKey: ['all-users'] });
      }

      if (isCurrentConversation) {
        console.log("matched conversationId", e.convoId || e.conversationId, selectedConversation?.conversationId);

        if (!messageModalDisplayed) {
          return;
        }

        //Append the message to the message array - check if it already exists first
        setMessageArray(prevMessages => {
          const existingIndex = prevMessages.findIndex(msg => msg._id === e._id);

          if (existingIndex !== -1) {
            // Message already exists, just update it if needed
            const existingMessage = prevMessages[existingIndex];
            const needsUpdate = !existingMessage.received || !existingMessage.seen;

            if (!needsUpdate) {
              // Already in correct state, return same array
              return prevMessages;
            }

            // Update existing message using GiftedChat.append pattern for immediate UI update
            // Preserve seenBy array if it exists in the incoming message
            const updatedMessage = {
              ...existingMessage,
              ...e,
              seen: true,
              received: true,
              seenBy: (e as any).seenBy || existingMessage.seenBy || [],
            } as ExtendedMessage;
            const messagesWithoutOld = prevMessages.filter(msg => msg._id !== e._id);
            return GiftedChat.append(messagesWithoutOld, [updatedMessage]) as ExtendedMessage[];
          }

          // New message - append it using GiftedChat.append
          return GiftedChat.append(prevMessages, [e]) as ExtendedMessage[];
        });

        // Mark message as seen if modal is displayed (user is viewing the conversation)
        if (messageModalDisplayed && selectedConversation?.conversationId) {
          const messageId = String(e._id);

          // Validate message ID is a valid UUID (not a system message)
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(messageId)) {
            console.warn('👁️ [ChatContainer] Skipping mark seen for non-UUID message:', messageId);
          } else {
            // Validate conversationId
            const conversationId = selectedConversation.conversationId;
            if (!conversationId || conversationId === "" || conversationId === "pendingMessages" || conversationId.startsWith('system-')) {
              console.warn('👁️ [ChatContainer] Invalid conversationId, skipping mark seen:', conversationId);
            } else {
              // Only mark as seen if we haven't already marked it
              if (!markedSeenRef.current.has(messageId)) {
                markedSeenRef.current.add(messageId);
                socket.emit("mark seen", messageId, conversationId);
                console.log('👁️ [ChatContainer] Marking received message as seen:', {
                  messageId,
                  conversationId,
                });
              }
            }
          }
        }
      }
    };
    responseHandlerRef.current = responseHandler;
    socket.on("response", responseHandler);
    const handleAcknowledge = (e: { id: string; conversationId?: string; status?: string; reason?: string }) => {
      console.log('✅ [ChatContainer] Acknowledge event received at', new Date().toISOString(), ':', e);

      if (e?.status === 'error') {
        console.warn('❌ [ChatContainer] Message acknowledgement error:', e.reason);
        return;
      }

      // Only update conversation ID if it's actually different from what we have
      // Don't update on every message send - only when starting a new conversation
      if (e.conversationId) {
        const currentConversationId = conversationId || selectedConversation?.conversationId;

        // Only update if conversation ID actually changed (new conversation)
        if (e.conversationId !== currentConversationId) {
          setConversationId(e.conversationId);
          dispatch({
            type: UPDATE_CONVERSATION_ID,
            payload: { conversationId: e.conversationId },
          });

          const currentSelected = selectedConversationRef.current;
          const targetUserId = currentSelected?.id || currentSelected?._id;
          if (targetUserId) {
            contextDispatch({
              type: 'UPDATE_CONVERSATION_ID',
              payload: {
                userId: targetUserId,
                conversationId: e.conversationId,
              },
            });
          }
        }
        // If conversation ID is the same, do nothing - don't trigger unnecessary updates
      }

      // Update the message using GiftedChat.append to ensure immediate UI update
      setMessageArray(prevMessages => {
        console.log('✅ [ChatContainer] Current message array length before acknowledge update:', prevMessages.length);

        // First try to find by exact ID match
        let messageIndex = prevMessages.findIndex(msg => msg._id === e.id);
        console.log('✅ [ChatContainer] Initial message search result:', {
          found: messageIndex !== -1,
          index: messageIndex,
          searchedId: e.id,
        });

        // If not found and we have a conversationId, try to find the most recent pending message
        // This handles the case where backend generates a new UUID for the message
        if (messageIndex === -1 && e.conversationId) {
          console.log(`✅ [ChatContainer] Message with ID ${e.id} not found by exact match. Searching for most recent pending message in conversation ${e.conversationId}.`);
          const conversationId = e.conversationId;
          const pendingMessages = prevMessages.filter(msg =>
            msg.pending &&
            ((msg as any).conversationId === conversationId ||
              selectedConversation?.conversationId === conversationId)
          );

          console.log('✅ [ChatContainer] Fallback: searching pending messages:', {
            pendingCount: pendingMessages.length,
            conversationId: conversationId,
          });

          if (pendingMessages.length > 0) {
            // Get the most recent pending message (first in array since GiftedChat is inverted)
            const mostRecentPending = pendingMessages[0];
            messageIndex = prevMessages.findIndex(msg => msg._id === mostRecentPending._id);
            console.log('✅ [ChatContainer] Found pending message to update via fallback:', {
              oldId: mostRecentPending._id,
              newId: e.id,
              messageText: mostRecentPending.text?.substring(0, 50),
            });
          }
        }

        if (messageIndex === -1) {
          // Message not found, return same array reference
          console.warn('✅ [ChatContainer] Message not found for acknowledge event:', {
            searchedId: e.id,
            conversationId: e.conversationId,
            availableIds: prevMessages.slice(0, 5).map(m => m._id),
          });
          return prevMessages;
        }

        const existingMessage = prevMessages[messageIndex];
        console.log('✅ [ChatContainer] Found message to update:', {
          messageId: existingMessage._id,
          currentState: {
            pending: existingMessage.pending,
            sent: existingMessage.sent,
            received: existingMessage.received,
          },
          messageText: existingMessage.text?.substring(0, 50),
        });

        // Check if update is actually needed - only skip if already acknowledged (sent + received, not pending)
        const isAlreadyAcknowledged = !existingMessage.pending && existingMessage.sent && existingMessage.received;
        if (isAlreadyAcknowledged && existingMessage._id === e.id) {
          // Already in the desired acknowledged state with matching ID, skip update
          console.log('✅ [ChatContainer] Message already in acknowledged state, skipping update.');
          return prevMessages;
        }

        // Always create a completely new message object to ensure GiftedChat detects the change
        // This is critical for UI updates - GiftedChat needs new object references
        // Using Object.assign to create a truly new object reference
        const updatedMessage: ExtendedMessage = Object.assign({}, existingMessage, {
          _id: e.id, // Update to the database ID if different
          pending: false,
          sent: true,
          received: true,
        });

        console.log('✅ [ChatContainer] Updating message with single tick (acknowledged):', {
          oldId: existingMessage._id,
          newId: updatedMessage._id,
          oldState: {
            pending: existingMessage.pending,
            sent: existingMessage.sent,
            received: existingMessage.received,
          },
          newState: {
            pending: updatedMessage.pending,
            sent: updatedMessage.sent,
            received: updatedMessage.received,
          },
          messageText: updatedMessage.text?.substring(0, 50),
        });

        // Use GiftedChat.append pattern: remove old message and append updated one
        // This ensures GiftedChat properly detects and renders the change immediately
        const messagesWithoutOld = prevMessages.filter(msg => msg._id !== existingMessage._id);
        const updatedArray = GiftedChat.append(messagesWithoutOld, [updatedMessage]) as ExtendedMessage[];

        console.log(`✅ [ChatContainer] Message array updated successfully. New length: ${updatedArray.length}.`);
        return updatedArray;
      });
    };

    socket.on("acknowledge", handleAcknowledge);

    // Handle seen event confirmation (when we mark a message as seen)
    const handleSeen = (e: { id: string; conversationId?: string; seenBy?: string; timestamp?: string }) => {
      console.log('👁️ [ChatContainer] Seen event received (confirmation):', e);

    // Update the message to reflect it's been seen
      setMessageArray(prevMessages => {
        const messageIndex = prevMessages.findIndex(msg => msg._id === e.id);
        if (messageIndex === -1) {
          return prevMessages;
        }

        const existingMessage = prevMessages[messageIndex];
        const updatedMessage: ExtendedMessage = Object.assign({}, existingMessage, {
          seen: true,
          received: true,
          seenBy: existingMessage.seenBy
            ? [...new Set([...existingMessage.seenBy, e.seenBy || userData?.id].filter(Boolean))]
            : e.seenBy ? [e.seenBy] : [],
        });

        const messagesWithoutOld = prevMessages.filter(msg => msg._id !== e.id);
        return GiftedChat.append(messagesWithoutOld, [updatedMessage]) as ExtendedMessage[];
      });
    };

    // Handle message seen event from other participants (for group chats)
    const handleMessageSeen = (e: { messageIds: string[]; conversationId?: string; seenBy?: string; timestamp?: string }) => {
      console.log('👁️ [ChatContainer] Message seen event received (from other participant):', e);

      // Only process if it's for the current conversation
      if (e.conversationId && e.conversationId !== selectedConversation?.conversationId) {
        return;
      }

      // Update messages to reflect that another user has seen them
      setMessageArray(prevMessages => {
        const messageIdsSet = new Set(e.messageIds);
        let hasUpdates = false;

        const updatedMessages = prevMessages.map(msg => {
          if (messageIdsSet.has(String(msg._id))) {
            hasUpdates = true;
            const existingSeenBy = (msg as ExtendedMessage).seenBy || [];
            const updatedSeenBy = e.seenBy && !existingSeenBy.includes(e.seenBy)
              ? [...existingSeenBy, e.seenBy]
              : existingSeenBy;

            return Object.assign({}, msg, {
              seenBy: updatedSeenBy,
            } as ExtendedMessage);
          }
          return msg;
        });

        // Only return new array if there were actual updates
        return hasUpdates ? updatedMessages : prevMessages;
      });
    };

    socket.on("seen", handleSeen);
    socket.on("message seen", handleMessageSeen);

    // Create and store the delivered handler
    const deliveredHandler = (e: { id: string; conversationId?: string }) => {
      console.log('📬 [ChatContainer] Delivered event received:', {
        messageId: e.id,
        conversationId: e.conversationId,
        currentConversationId: selectedConversation?.conversationId,
        timestamp: new Date().toISOString(),
      });

      // Update the message using GiftedChat.append to ensure immediate UI update
      setMessageArray(prevMessages => {
        console.log('📬 [ChatContainer] Current message array length:', prevMessages.length);

        // First try to find by exact ID match
        let messageIndex = prevMessages.findIndex(msg => msg._id === e.id);
        console.log('📬 [ChatContainer] Initial message search result:', {
          found: messageIndex !== -1,
          index: messageIndex,
          searchedId: e.id,
        });

        // If not found and we have a conversationId, try to find the most recent pending message
        // This handles the case where backend generates a new UUID for the message
        if (messageIndex === -1 && e.conversationId) {
          const conversationId = e.conversationId;
          const pendingMessages = prevMessages.filter(msg =>
            msg.pending &&
            ((msg as any).conversationId === conversationId ||
              selectedConversation?.conversationId === conversationId)
          );

          console.log('📬 [ChatContainer] Fallback: searching pending messages:', {
            pendingCount: pendingMessages.length,
            conversationId: conversationId,
          });

          if (pendingMessages.length > 0) {
            // Get the most recent pending message (first in array since GiftedChat is inverted)
            const mostRecentPending = pendingMessages[0];
            messageIndex = prevMessages.findIndex(msg => msg._id === mostRecentPending._id);
            console.log('📬 [ChatContainer] Found pending message to update:', {
              oldId: mostRecentPending._id,
              newId: e.id,
              messageText: mostRecentPending.text?.substring(0, 50),
            });
          }
        }

        if (messageIndex === -1) {
          // Message not found, return same array reference
          console.warn('📬 [ChatContainer] Message not found for delivered event:', {
            searchedId: e.id,
            conversationId: e.conversationId,
            availableIds: prevMessages.slice(0, 5).map(m => m._id),
          });
          return prevMessages;
        }

        const existingMessage = prevMessages[messageIndex];
        console.log('📬 [ChatContainer] Found message to update:', {
          messageId: existingMessage._id,
          currentState: {
            pending: existingMessage.pending,
            sent: existingMessage.sent,
            received: existingMessage.received,
            delivered: (existingMessage as any).delivered,
          },
          messageText: existingMessage.text?.substring(0, 50),
        });

        // Check if update is actually needed - only skip if already delivered
        // Always update when delivered event comes to ensure UI reflects the change
        const isAlreadyDelivered = !existingMessage.pending && existingMessage.sent && (existingMessage as any).delivered;
        if (isAlreadyDelivered && existingMessage._id === e.id) {
          // Already in the desired delivered state with matching ID, skip update
          console.log('📬 [ChatContainer] Message already in delivered state, skipping update');
          return prevMessages;
        }

        // Always create a completely new message object to ensure GiftedChat detects the change
        // This is critical for UI updates - GiftedChat needs new object references
        // Using Object.assign to create a truly new object reference
        const updatedMessage: ExtendedMessage = Object.assign({}, existingMessage, {
          _id: e.id, // Update to the database ID
          pending: false,
          sent: true,
          delivered: true, // Explicitly set delivered for double ticks
          received: existingMessage.received || true,
        });

        console.log('📬 [ChatContainer] Updating message with double ticks (delivered):', {
          oldId: existingMessage._id,
          newId: updatedMessage._id,
          oldState: {
            pending: existingMessage.pending,
            sent: existingMessage.sent,
            delivered: (existingMessage as any).delivered,
            received: existingMessage.received,
          },
          newState: {
            pending: updatedMessage.pending,
            sent: updatedMessage.sent,
            delivered: updatedMessage.delivered,
            received: updatedMessage.received,
          },
          messageText: updatedMessage.text?.substring(0, 50),
        });

        // Use GiftedChat.append pattern: remove old message and append updated one
        // This ensures Gifted Chat properly detects and renders the change immediately
        const messagesWithoutOld = prevMessages.filter(msg => msg._id !== existingMessage._id);
        const updatedArray = GiftedChat.append(messagesWithoutOld, [updatedMessage]) as ExtendedMessage[];

        console.log('📬 [ChatContainer] Message array updated successfully:', {
          previousLength: prevMessages.length,
          newLength: updatedArray.length,
          updatedMessageId: updatedMessage._id,
        });

        return updatedArray;
      });
    };
    deliveredHandlerRef.current = deliveredHandler;
    socket.on("delivered", deliveredHandler);

    return () => {
      // Remove only the specific listeners we registered using stored references
      if (responseHandlerRef.current) {
        socket.off("response", responseHandlerRef.current);
        responseHandlerRef.current = null;
      }
      socket.off("acknowledge", handleAcknowledge);
      socket.off("seen", handleSeen);
      socket.off("message seen", handleMessageSeen);
      if (deliveredHandlerRef.current) {
        socket.off("delivered", deliveredHandlerRef.current);
        deliveredHandlerRef.current = null;
      }
    }
  }, [socket, contextDispatch, dispatch, messageModalDisplayed, selectedConversation?.conversationId, userData?.id])

  useEffect(() => {
    if (!messageModalDisplayed || !selectedConversation?.conversationId) {
      return;
    }

    // Validate conversationId - must be a valid ObjectId/UUID, not empty or "pendingMessages"
    const conversationIdentifier = selectedConversation.conversationId;
    if (!conversationIdentifier ||
      conversationIdentifier === "" ||
      conversationIdentifier === "pendingMessages" ||
      conversationIdentifier.startsWith('system-')) {
      console.warn('👁️ [ChatContainer] Invalid conversationId, skipping mark seen:', conversationIdentifier);
      return;
    }

    const unseenMessages = messageArray.filter(msg => {
      if (!msg?._id) return false;

      // Filter out system messages (they have system: true or _id starting with 'system-')
      if (msg.system || String(msg._id).startsWith('system-')) {
        return false;
      }

      // Filter out messages without valid UUID format (system messages won't be valid UUIDs)
      const messageId = String(msg._id);
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(messageId)) {
        console.warn('👁️ [ChatContainer] Skipping non-UUID message ID:', messageId);
        return false;
      }

      if (markedSeenRef.current.has(messageId)) return false;
      const isFromSelf = msg.user?._id === userData?.id;
      const alreadySeen = msg.received || msg.seen;
      return !isFromSelf && !alreadySeen;
    });

    if (!unseenMessages.length) {
      return;
    }

    // Batch mark seen calls for better performance (backend supports array of message IDs)
    const messageIds = unseenMessages.map(msg => {
      const messageId = String(msg._id);
      markedSeenRef.current.add(messageId);
      return messageId;
    });

    console.log('👁️ [ChatContainer] Marking messages as seen:', {
      messageCount: messageIds.length,
      messageIds: messageIds.slice(0, 5), // Log first 5 for debugging
      conversationId: conversationIdentifier,
    });

    // Send batch request if we have multiple messages, otherwise send single message
    if (messageIds.length > 0) {
      if (messageIds.length === 1) {
        // Single message - send as string for backward compatibility
        socket.emit("mark seen", messageIds[0], conversationIdentifier);
      } else {
        // Multiple messages - send as array for batch processing
        socket.emit("mark seen", messageIds, conversationIdentifier);
        console.log('👁️ [ChatContainer] Batch marking', messageIds.length, 'messages as seen');
      }
    }

    // Only update messages that need to be marked as seen - avoid recreating entire array
    setMessageArray(prev => {
      const messageIdsToUpdate = new Set(unseenMessages.map(u => u._id));

      // First, check if any updates are needed
      const needsUpdate = prev.some(msg => {
        if (!messageIdsToUpdate.has(msg._id)) return false;
        return !msg.received || !msg.seen;
      });

      if (!needsUpdate) {
        // No changes needed, return same array reference
        return prev;
      }

      // Create new array only with updated messages
      return prev.map(msg => {
        if (messageIdsToUpdate.has(msg._id) && (!msg.received || !msg.seen)) {
          return { ...msg, received: true, seen: true } as ExtendedMessage;
        }
        return msg;
      });
    });

    const targetUserId = selectedConversation.id || selectedConversation._id;
    if (targetUserId) {
      contextDispatch({
        type: 'UPDATE_BADGE',
        userId: targetUserId,
        amount: -999,
        event: { convoId: conversationIdentifier },
      });
    }

    // Remove this invalidation - we're managing messages locally via setMessageArray
    // No need to invalidate query cache since we're updating state directly
    // queryClient.invalidateQueries({ queryKey: ['messageArray', conversationIdentifier] });
  }, [messageModalDisplayed, messageArray, selectedConversation?.conversationId, contextDispatch, queryClient, socket, selectedConversation?.id, selectedConversation?._id, userData?.id]);

  useEffect(() => {
    if (!selectedConversation?.conversationId) {
      setMessageArray([
        {
          _id: 'system-1',
          text: 'Send a message to start a conversation',
          createdAt: new Date(),
          system: true,
          user: { _id: 'system' },
        }
      ]);
      dispatch({ type: LOADED_MESSAGES });
      return;
    }
  }, [selectedConversation, dispatch])

  // Debounce LOADED_MESSAGES dispatch to prevent excessive re-renders when messages update
  const loadedMessagesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (messageArray && messageArray.length > 0) {
      // Clear existing timeout
      if (loadedMessagesTimeoutRef.current) {
        clearTimeout(loadedMessagesTimeoutRef.current);
      }
      // Debounce the dispatch to prevent firing on every message addition
      loadedMessagesTimeoutRef.current = setTimeout(() => {
        dispatch({ type: LOADED_MESSAGES });
      }, 100);
    }

    return () => {
      if (loadedMessagesTimeoutRef.current) {
        clearTimeout(loadedMessagesTimeoutRef.current);
      }
    };
  }, [messageArray.length, dispatch]); // Only depend on length, not array reference

  // Memoize conversation key to ensure query refetches when conversation changes
  const stableConversationKey = useMemo(() => {
    const key = selectedConversation?.conversationId || conversationId || selectedConversation?._id || selectedConversation?.id || "pendingMessages";
    console.log('🔑 ChatContainer: Conversation key updated:', {
      key,
      selectedConversationConversationId: selectedConversation?.conversationId,
      conversationIdState: conversationId,
      selectedConversation_id: selectedConversation?._id,
      selectedConversationId: selectedConversation?.id
    });
    return key;
  }, [selectedConversation?.conversationId, conversationId, selectedConversation?._id, selectedConversation?.id]);

  // Determine if we have a valid conversationId for the query
  const isValidConversationId: boolean = useMemo(() => {
    const convId = selectedConversation?.conversationId || conversationId;
    // Return a strict boolean: true only if convId exists, is not empty, and is not "pendingMessages"
    if (!convId || convId === "" || convId === "pendingMessages") {
      return false;
    }
    return true;
  }, [selectedConversation?.conversationId, conversationId]);

  const { data: messages } = useQuery<any>({
    queryKey: ['messageArray', stableConversationKey],
    queryFn: async () => {
      // Ensure we have a conversationId before fetching
      const fetchConversationId = selectedConversation?.conversationId || conversationId;
      if (!fetchConversationId || fetchConversationId === "" || fetchConversationId === "pendingMessages") {
        console.warn('⚠️ No valid conversationId available for fetching messages:', fetchConversationId);
        return [];
      }
      console.log('📥 Fetching messages for conversation:', fetchConversationId);
      const data = await apis.getConversation({ conversationId: fetchConversationId }).then(res => res.data.data.messages)
        .catch(() => {
          dispatch({ type: LOADED_MESSAGES });
          return [];
        })
        .finally(() => {
          dispatch({ type: LOADED_MESSAGES });
        })

      // Process messages and update markedSeenRef
      if (data && Array.isArray(data)) {
        data.forEach(message => {
          if (!message?._id) {
            return;
          }
          const messageId = String(message._id);
          if ((message as ExtendedMessage).received || (message as ExtendedMessage).seen) {
            markedSeenRef.current.add(messageId);
          }
        });
      }

      return data || [];
    },
    enabled: isValidConversationId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - use cached data on subsequent opens
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (formerly cacheTime)
  });

  // Sync messageArray from query data (both fresh and cached)
  // This ensures messages are restored when reopening a conversation
  useEffect(() => {
    if (!isValidConversationId) {
      return;
    }

    if (messages !== undefined) {
      if (Array.isArray(messages) && messages.length > 0) {
        console.log('📥 Syncing messages from query data (cache or fresh):', messages.length, 'messages');
        setMessageArray(messages as ExtendedMessage[]);
      } else if (Array.isArray(messages) && messages.length === 0) {
        // Empty array from query means no messages - set empty array
        console.log('📥 No messages found for conversation');
        setMessageArray([]);
      }
    }
  }, [messages, isValidConversationId]);

  // Track previous conversation key to prevent unnecessary invalidations
  const prevConversationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const currentKey = stableConversationKey;

    // Only invalidate if the conversation key actually changed (switching conversations)
    if (currentKey && currentKey !== prevConversationKeyRef.current && currentKey !== "pendingMessages") {
      const previousKey = prevConversationKeyRef.current;
      prevConversationKeyRef.current = currentKey;

      // Only invalidate if we're switching to a different conversation (not just opening the same one)
      if (previousKey && previousKey !== currentKey) {
        console.log('📥 Conversation switched, invalidating previous query:', previousKey);
        queryClient.invalidateQueries({ queryKey: ['messageArray', previousKey] });
      }
    }
  }, [stableConversationKey, queryClient]);

  const handleLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    setLayoutWidth(width);
  };

  // Memoize messages - only create new reference when array content actually changes
  // Create a hash of message IDs and their statuses to detect any message updates
  // This ensures GiftedChat re-renders when message statuses change (pending -> sent -> delivered)
  const messageStatusHash = useMemo(() => {
    // Create a hash string from all message IDs and their key status properties
    // This changes whenever any message's status is updated
    // Include seenBy for group messaging support
    return messageArray.map(msg => {
      const extended = msg as ExtendedMessage;
      const seenByHash = extended.seenBy ? extended.seenBy.sort().join(',') : '';
      return `${msg._id}:${extended.pending ? 'p' : ''}${extended.sent ? 's' : ''}${extended.received ? 'r' : ''}${extended.delivered ? 'd' : ''}${seenByHash ? `:seenBy[${seenByHash}]` : ''}`;
    }).join('|');
  }, [messageArray]);

  const memoizedMessages = useMemo(() => {
    return messageArray as IMessage[];
  }, [
    messageArray.length,
    messageArray[0]?._id, // First message ID (most recent in GiftedChat's inverted list)
    messageArray[messageArray.length - 1]?._id, // Last message ID (oldest)
    messageStatusHash, // Hash of all message statuses - changes when any message is updated
  ]);

  // Memoize user object to prevent re-creation
  const memoizedUser = useMemo(() => ({
    _id: userData.id,
    avatar: userData.avatar?.img
  }), [userData.id, userData.avatar?.img]);

  // Custom renderer for message bubbles to ensure text wrapping
  const renderBubble = useCallback((props: any) => (
    <Bubble
      {...props}
      wrapperStyle={{
        right: {
          maxWidth: layoutWidth > 0 ? layoutWidth * 0.8 : '80%',
          paddingHorizontal: 12,
          paddingVertical: 8,
          ...props.wrapperStyle?.right,
        },
        left: {
          maxWidth: layoutWidth > 0 ? layoutWidth * 0.8 : '80%',
          paddingHorizontal: 12,
          paddingVertical: 8,
          ...props.wrapperStyle?.left,
        },
      }}
    />
  ), [layoutWidth]);

  // Handler for reference button clicks
  const handleReferencePress = useCallback((reference: ParsedReference) => {
    try {
      switch (reference.type) {
        case 'data':
          (navigation as any).navigate('DataBrowser', {
            requestId: reference.id,
            title: reference.title,
          });
          break;
        case 'project':
          if (Platform.OS === 'web') {
            (navigation as any).replace('IndividualProjectPage', {
              projectId: reference.id,
              title: reference.title,
            });
          } else {
            (navigation as any).navigate('IndividualProjectPage', {
              project: {
                title: reference.title,
                _id: reference.id,
              },
            });
          }
          break;
        case 'item':
          // Navigate to generic data item screen
          (navigation as any).navigate('GenericDataItemScreen', {
            dataId: reference.id,
            contentId: reference.id,
          });
          break;
        case 'calendar':
          // Navigate to calendar screen - you may need to adjust this based on your calendar route
          (navigation as any).navigate('Calendar', {
            eventId: reference.id,
          });
          break;
        default:
          console.warn('Unknown reference type:', reference.type);
      }
    } catch (error) {
      console.error('Error navigating to reference:', error);
    }
  }, [navigation]);

  // Custom renderer for message text to ensure wrapping and parse references
  const renderMessageText = useCallback((props: any) => {
    const messageText = props.currentMessage?.text || '';
    const isRight = props.position === 'right';
    const textColor = isRight
      ? (props.textStyle?.right?.color || '#FFFFFF')
      : (props.textStyle?.left?.color || '#000000');

    return (
      <View style={{ flex: 1, paddingHorizontal: 10, paddingVertical: 6 }}>
        <ParsedTextWithReferences
          text={messageText}
          textStyle={{
            ...(isRight ? props.textStyle?.right : props.textStyle?.left),
            color: textColor,
          }}
          theme={theme}
          onReferencePress={handleReferencePress}
        />
      </View>
    );
  }, [theme, handleReferencePress]);

  return (
    <View onLayout={handleLayout} style={{ flex: 1, minWidth: isTablet() ? 580: 360, height: '100%' }}>
      <ErrorBoundary>
        <GiftedChat
        listProps={{
          showsVerticalScrollIndicator: false,
          contentContainerStyle: {
            paddingHorizontal: 8,
          },
        }}
          messages={memoizedMessages}
          isUsernameVisible
          key={stableConversationKey}
          // minInputToolbarHeight={40}
          {...(typeof window !== 'undefined' && { showAvatarForEveryMessage: false } as any)}
          renderChatEmpty={() => messageLoading ? <View style={{ paddingVertical: "70%" }}><Spinner size='large' color='rgb(90,192,234)' /></View> : null}
          renderComposer={() => (null)}
          renderBubble={renderBubble}
          renderMessageText={renderMessageText}
          onSend={messages => handleMessage({
            messages,
            conversationIdState: conversationId,
            data,
            setData,
            selectedConversation: {
              id: selectedConversation?.id || selectedConversation?._id,
              participantId: selectedConversation?.participantId,
              conversationId: conversationId,
            },
            setMessageArray: setMessageArray as unknown as React.Dispatch<React.SetStateAction<IMessage[]>>,
            socket
          })}
          renderInputToolbar={(props: InputToolbarProps<any>) =>
            <ErrorBoundary>
              <InputToolbar
                {...(props as any)}
                targetUser={selectedConversation}
                maxHeight={300}
                removeTools={false}
                containerStyle={{ maxHeight: "27%", paddingBottom: 0, paddingTop: 0 }}
                placeHolder="Enter your message here..."
                multiline
                inverse
                removeAttach
                forceColumn
                staticPress
              />
            </ErrorBoundary>
          }
          renderSystemMessage={props => {
            return (
              <SystemMessage
                {...props}
                textStyle={{ ...(props?.textStyle as any), fontFamily: FONTS.regular }}
              />
            );
          }}
          renderMessageImage={props => {
            return (
              <MessageImage
                {...props}
                containerStyle={props.containerStyle as any}
                imageStyle={{
                  width: widthPercentageToDP(50),
                  height: heightPercentageToDP(20),
                  borderRadius: 13,
                  margin: 3,
                  resizeMode: 'cover',
                }}
              />
            );
          }}
          user={memoizedUser}
          renderAvatar={() => (
            <Image
              source={{ uri: typeof selectedConversation?.avatar === 'string' ? selectedConversation?.avatar : (selectedConversation?.avatar as any)?.img }}
              style={{ minHeight: 35, minWidth: 35, borderRadius: 20 }}
            />
          )}
          renderDay={(props: any) => {
            // Provide a simple Day renderer for web compatibility
            if (!props?.currentMessage?.createdAt) return null;
            const date = new Date(props.currentMessage.createdAt);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let dateText = '';
            if (date.toDateString() === today.toDateString()) {
              dateText = 'Today';
            } else if (date.toDateString() === yesterday.toDateString()) {
              dateText = 'Yesterday';
            } else {
              dateText = date.toLocaleDateString();
            }

            return (
              <View style={{ alignItems: 'center', marginVertical: 10 }}>
                <Text style={{ fontSize: 12, color: '#999' }}>{dateText}</Text>
              </View>
            );
          }}
          renderTime={(props: any) => {
            // Provide a simple Time renderer for web compatibility
            if (!props?.currentMessage?.createdAt) return null;
            const date = new Date(props.currentMessage.createdAt);
            const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            // Check if message is sent by current user (white text) or received (gray text)
            const isSentMessage = props.currentMessage.user?._id === memoizedUser._id;
            const textColor = isSentMessage ? '#FFFFFF' : '#999';

            return (
              <View style={{ paddingHorizontal: 8, paddingVertical: 4 }}>
                <Text style={{ fontSize: 11, color: textColor }}>{timeString}</Text>
              </View>
            );
          }}
        />
      </ErrorBoundary>
    </View>
  )
}
export default ChatContainer;